import React, { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { CheckBadgeIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import { cancelFolderTask, getFolderTasks } from "../../../services/questionService";
import HelpButton from "../../Help/HelpButton";
import TasksGuide from "../../Help/guides/TasksGuide";
import { UserContext } from "../../../utils/contexts/UserContext";
import { canAssignIn } from "../../../utils/folders";
import { isCanceledError } from "../../../utils/examError";
import { cardClass, focusRing, Spinner } from "../matrix/MatrixParts";
import TaskCard from "./TaskCard";
import TaskDialog from "./TaskDialog";
import { personName, taskErrorMessage, taskTitle } from "./taskModel";

const primaryClass = `inline-flex h-10 items-center gap-1.5 whitespace-nowrap rounded-lg bg-blue-600 px-4 text-sm font-medium text-white shadow-sm transition duration-200 hover:bg-blue-700 max-sm:h-11 max-sm:flex-1 max-sm:justify-center ${focusRing}`;
const secondaryClass = `inline-flex h-10 items-center gap-1.5 whitespace-nowrap rounded-lg bg-white px-4 text-sm font-medium text-slate-700 shadow-sm ring-1 ring-inset ring-slate-300 transition duration-200 hover:bg-slate-50 max-sm:h-11 max-sm:flex-1 max-sm:justify-center ${focusRing}`;

/**
 * The Tasks tab of a subject (TZ 2.2). Its moderator and admins see every task with its assignee, give a task to write
 * questions of a matrix row or to review chosen authors' or chosen questions, change it and cancel it. An author or an
 * expert sees their own, with the way to work on each.
 */
export default function FolderTasks({ folder }) {
  const [t] = useTranslation();
  const { user } = useContext(UserContext) || {};
  const canAssign = canAssignIn(folder?.role);
  const [tasks, setTasks] = useState(null);
  const [error, setError] = useState("");
  const [showCancelled, setShowCancelled] = useState(false);
  const [dialog, setDialog] = useState({ open: false, task: null, kind: "write" });
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    if (!folder) return undefined;
    const controller = new AbortController();
    setError("");
    getFolderTasks(folder.uuid, { includeCancelled: canAssign && showCancelled, signal: controller.signal })
      .then((list) => setTasks(Array.isArray(list) ? list : []))
      .catch((err) => {
        if (isCanceledError(err)) return;
        setTasks([]);
        setError(taskErrorMessage(t, err, "folders.tasks.load_failed"));
      });
    return () => controller.abort();
  }, [folder, canAssign, showCancelled, reloadKey, t]);

  const handleSaved = (saved, created) => {
    setDialog((d) => ({ ...d, open: false }));
    toast.success(created ? t("folders.tasks.created", { name: personName(saved?.assignee) }) : t("folders.tasks.saved"));
    reload();
  };

  const handleCancel = async (task) => {
    const name = taskTitle(t, task);
    if (!window.confirm(t("folders.tasks.cancel_confirm", { name, person: personName(task.assignee) }))) return;
    try {
      await cancelFolderTask(folder.uuid, task.uuid);
      toast.success(t("folders.tasks.cancelled"));
    } catch (err) {
      toast.error(taskErrorMessage(t, err, "folders.tasks.cancel_failed"));
    }
    reload();
  };

  const active = (tasks || []).filter((task) => !task.cancelled);
  const counts = {
    active: active.filter((task) => !task.done).length,
    done: active.filter((task) => task.done).length,
    overdue: active.filter((task) => task.overdue && !task.done).length,
  };

  let body;
  if (tasks === null) {
    body = (
      <div role="status" className="flex items-center justify-center gap-3 py-14 text-sm text-slate-500">
        <Spinner />
        {t("folders.tasks.loading")}
      </div>
    );
  } else if (error) {
    body = (
      <div className="flex flex-col items-center gap-3 py-12 text-center">
        <p role="alert" className="text-sm text-red-700">
          {error}
        </p>
        <button type="button" onClick={reload} className={secondaryClass}>
          {t("folders.tasks.retry")}
        </button>
      </div>
    );
  } else if (tasks.length === 0) {
    body = (
      <div className="mx-auto max-w-md py-12 text-center">
        <CheckBadgeIcon className="mx-auto h-10 w-10 text-slate-300" aria-hidden="true" />
        <p className="mt-3 text-sm font-medium text-slate-900">{canAssign ? t("folders.tasks.empty_title") : t("folders.tasks.empty_mine")}</p>
        {canAssign && <p className="mt-1 text-sm text-slate-500">{t("folders.tasks.empty_hint")}</p>}
      </div>
    );
  } else {
    body = (
      <ul className="space-y-3">
        {tasks.map((task) => (
          <li key={task.uuid}>
            <TaskCard
              task={task}
              showAssignee={canAssign}
              own={task.assignee?.uuid === user?.uuid}
              onEdit={canAssign ? (item) => setDialog({ open: true, task: item, kind: item.kind }) : undefined}
              onCancel={canAssign ? handleCancel : undefined}
            />
          </li>
        ))}
      </ul>
    );
  }

  return (
    <section aria-labelledby="folder-tasks-title" className={`${cardClass} p-4 sm:p-6`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          {/* The guide's "?" after the title (the section's own heading keeps its id), the row keeping its height. */}
          <div className="flex min-w-0 items-center gap-1.5">
            <h2 id="folder-tasks-title" className="min-w-0 text-lg font-semibold text-slate-900">
              {t("folders.tasks.title")}
            </h2>
            <HelpButton id="tasks" title={t("help.tasks.title")} className="-my-2 md:-my-1">
              <TasksGuide inSubject canAssign={canAssign} role={folder?.role} />
            </HelpButton>
          </div>
          <p className="mt-1 text-sm text-slate-500">{canAssign ? t("folders.tasks.subtitle_moderator") : t("folders.tasks.subtitle_mine")}</p>
          {active.length > 0 && (
            <p className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
              <span>{t("folders.tasks.count_active", { count: counts.active })}</span>
              {counts.done > 0 && <span className="text-emerald-700">{t("folders.tasks.count_done", { count: counts.done })}</span>}
              {counts.overdue > 0 && <span className="font-medium text-red-700">{t("folders.tasks.count_overdue", { count: counts.overdue })}</span>}
            </p>
          )}
        </div>
        {canAssign && (
          <div className="flex w-full flex-wrap gap-2 sm:w-auto">
            <button type="button" onClick={() => setDialog({ open: true, task: null, kind: "write" })} className={primaryClass}>
              <PencilSquareIcon className="h-4 w-4" aria-hidden="true" />
              {t("folders.tasks.new_write")}
            </button>
            <button type="button" onClick={() => setDialog({ open: true, task: null, kind: "review" })} className={secondaryClass}>
              <CheckBadgeIcon className="h-4 w-4" aria-hidden="true" />
              {t("folders.tasks.new_review")}
            </button>
          </div>
        )}
      </div>

      {canAssign && (
        <label className="mt-4 inline-flex cursor-pointer items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={showCancelled}
            onChange={(e) => setShowCancelled(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-600"
          />
          {t("folders.tasks.show_cancelled")}
        </label>
      )}

      <div className="mt-4">{body}</div>

      {canAssign && (
        <TaskDialog
          isOpen={dialog.open}
          folder={folder}
          task={dialog.task}
          kind={dialog.kind}
          onClose={() => setDialog((d) => ({ ...d, open: false }))}
          onSaved={handleSaved}
        />
      )}
    </section>
  );
}
